import React, { useState, useEffect } from 'react';
import axios from 'axios';

// Компонент для обрезки длинного текста с подсказкой
function TruncatedText({ text, maxLength = 20 }) {
  if (!text || text.length <= maxLength) {
    return <>{text}</>;
  }
  
  return (
    <span className="truncated-text-container">
      <span className="truncated-text">{text.substring(0, maxLength)}...</span>
      <span className="truncated-tooltip">{text}</span>
    </span>
  );
}

const ModerationPage = () => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState('');
  const [availableRoles, setAvailableRoles] = useState({});
  const [showRoleModal, setShowRoleModal] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [notifications, setNotifications] = useState([]);
  const [expandedUsers, setExpandedUsers] = useState(new Set());
  const [searchTerm, setSearchTerm] = useState('');
  // Режим просмотра для каждой строки пользователя: 'active' | 'archived'
  const [archivedViewByUser, setArchivedViewByUser] = useState({});

  // Функция для добавления уведомления
  const addNotification = (type, title, message) => {
    const id = Date.now() + Math.random();
    const notification = { id, type, title, message };
    
    setNotifications(prev => [...prev, notification]);
    
    // Автоматически удаляем уведомление через 5 секунд
    setTimeout(() => {
      removeNotification(id);
    }, 5000);
  };

  // Функция для удаления уведомления
  const removeNotification = (id) => {
    setNotifications(prev => prev.filter(n => n.id !== id));
  };

  // Функция для переключения раскрытия пользователя
  const toggleUserExpansion = (userId) => {
    setExpandedUsers(prev => {
      const newSet = new Set(prev);
      if (newSet.has(userId)) {
        newSet.delete(userId);
      } else {
        newSet.add(userId);
      }
      return newSet;
    });
    setArchivedViewByUser(prev => {
      if (!prev[userId]) {
        return { ...prev, [userId]: 'active' };
      }
      return prev;
    });
  };

  // Функция для переключения активации агента (отдельно от блокировки)
  const toggleAgentActivation = async (agentId, currentStatus) => {
    try {
      const response = await axios.post(`${API_URL}/admin/toggle-agent-activation`, {
        agent_id: agentId
      }, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('admin_token')}`
        }
      });

      if (response.data.success) {
        addNotification('success', 'Успех', `Агент ${!currentStatus ? 'запущен' : 'остановлен'}`);
        
        // Обновляем локальные данные
        setUsers(users.map(user => ({
          ...user,
          agents: user.agents.map(agent => 
            agent.id === agentId 
              ? { ...agent, is_active: response.data.is_active }
              : agent
          )
        })));
      }
    } catch (error) {
      console.error('Error toggling agent activation:', error);
      addNotification('error', 'Ошибка', 'Не удалось изменить статус агента');
    }
  };

  const API_URL = process.env.REACT_APP_API_URL;

  const restoreAgent = async (agentId) => {
    try {
      const token = localStorage.getItem('admin_token');
      const response = await axios.post(`${API_URL}/admin/agents/${agentId}/restore`, {}, {
        headers: { Authorization: `Bearer ${token}` },
        withCredentials: true
      });
      if (response.data.ok) {
        addNotification('success', 'Успех', 'Агент восстановлен');
        // Локально снять флаг archived
        setUsers(users.map(user => ({
          ...user,
          agents: user.agents.map(agent => agent.id === agentId ? { ...agent, archived: false } : agent)
        })));
      }
    } catch (e) {
      addNotification('error', 'Ошибка', 'Не удалось восстановить агента');
    }
  };

  useEffect(() => {
    fetchUsers();
    fetchAvailableRoles();
  }, []);

  const fetchUsers = async () => {
    try {
      const token = localStorage.getItem('admin_token');
      const response = await axios.get(`${API_URL}/admin/users/moderation`, {
        headers: { Authorization: `Bearer ${token}` },
        withCredentials: true
      });
      
      if (response.data.ok) {
        setUsers(response.data.users);
      }
    } catch (error) {
      addNotification(
        'error',
        'Ошибка загрузки',
        'Не удалось загрузить данные пользователей'
      );
    } finally {
      setLoading(false);
    }
  };

  const fetchAvailableRoles = async () => {
    try {
      const token = localStorage.getItem('admin_token');
      const response = await axios.get(`${API_URL}/admin/roles`, {
        headers: { Authorization: `Bearer ${token}` },
        withCredentials: true
      });
      
      if (response.data.ok) {
        setAvailableRoles(response.data.roles);
      }
    } catch (error) {
      addNotification(
        'error',
        'Ошибка загрузки ролей',
        'Не удалось загрузить доступные роли'
      );
    }
  };

  const toggleUserStatus = async (userId, currentStatus) => {
    try {
      const token = localStorage.getItem('admin_token');
      await axios.put(`${API_URL}/admin/users/${userId}/status`, {
        is_active: !currentStatus
      }, {
        headers: { Authorization: `Bearer ${token}` },
        withCredentials: true
      });
      
      // Обновляем локальные данные
      const updatedUsers = users.map(user => 
        user.id === userId 
          ? { ...user, is_active: !currentStatus }
          : user
      );
      setUsers(updatedUsers);
      
      const user = users.find(u => u.id === userId);
      addNotification(
        'success',
        'Статус изменен',
        `Пользователь ${user?.email} ${!currentStatus ? 'разблокирован' : 'заблокирован'}`
      );
    } catch (error) {
      addNotification(
        'error',
        'Ошибка изменения статуса',
        'Не удалось изменить статус пользователя'
      );
    }
  };

  // Функция для блокировки/разблокировки агента (бан/разбан)
  const toggleAgentStatus = async (agentId, isCurrentlyBlocked) => {
    try {
      const token = localStorage.getItem('admin_token');
      const response = await axios.post(`${API_URL}/admin/agents/${agentId}/block`, {
        blocked: !isCurrentlyBlocked
      }, {
        headers: { Authorization: `Bearer ${token}` },
        withCredentials: true
      });
      
      if (response.data.ok) {
        const { blocked_by_admin, is_active } = response.data;
      // Обновляем локальные данные
      setUsers(users.map(user => ({
        ...user,
        agents: user.agents.map(agent => 
          agent.id === agentId 
              ? { ...agent, blocked_by_admin, is_active }
            : agent
        )
      })));
      
        setMessage(`Бот ${blocked_by_admin ? 'заблокирован' : 'разблокирован'}`);
      }
      setTimeout(() => setMessage(''), 3000);
    } catch (error) {
      setMessage('Ошибка изменения статуса бота');
    }
  };

  const openRoleModal = (user) => {
    setSelectedUser(user);
    setShowRoleModal(true);
  };

  const updateUserRole = async (newRole) => {
    try {
      const token = localStorage.getItem('admin_token');
      const response = await axios.post(`${API_URL}/admin/users/${selectedUser.id}/role`, {
        role: newRole
      }, {
        headers: { Authorization: `Bearer ${token}` },
        withCredentials: true
      });
      
      if (response.data.ok) {
        // Обновляем локальные данные в реальном времени
        const roleData = availableRoles[newRole];
        const updatedUsers = users.map(user => 
          user.id === selectedUser.id 
            ? { 
                ...user, 
                user_role: newRole,
                role_name: roleData?.name || newRole,
                max_agents: roleData?.max_agents || 0,
                max_messages_per_month: roleData?.max_messages_per_month || 0,
                ...response.data.user 
              }
            : user
        );
        setUsers(updatedUsers);
        
        // Показываем уведомление об успехе
        addNotification(
          'success',
          'Роль изменена',
          `Роль пользователя ${selectedUser.email} изменена на "${availableRoles[newRole]?.name || newRole}"`
        );
        
        setShowRoleModal(false);
        setSelectedUser(null);
      }
    } catch (error) {
      // Показываем уведомление об ошибке
      addNotification(
        'error',
        'Ошибка изменения роли',
        'Не удалось изменить роль пользователя. Попробуйте еще раз.'
      );
    }
  };

  const resetUserLimits = async (userId) => {
    if (!window.confirm('Сбросить месячные лимиты сообщений для этого пользователя?')) {
      return;
    }
    
    try {
      const token = localStorage.getItem('admin_token');
      const response = await axios.post(`${API_URL}/admin/users/${userId}/reset-limits`, {}, {
        headers: { Authorization: `Bearer ${token}` },
        withCredentials: true
      });
      
      if (response.data.ok) {
        // Обновляем локальные данные
        const updatedUsers = users.map(user => 
          user.id === userId 
            ? { ...user, current_messages_count: 0 }
            : user
        );
        setUsers(updatedUsers);
        
        addNotification(
          'success',
          'Лимиты сброшены',
          'Месячные лимиты сообщений успешно сброшены'
        );
      }
    } catch (error) {
      addNotification(
        'error',
        'Ошибка',
        'Не удалось сбросить лимиты'
      );
    }
  };

  if (loading) {
    return <div className="admin-loading">Загрузка пользователей...</div>;
  }

  return (
    <div className="moderation-page page-container">
      <div className="page-header-fixed">
        {message && (
          <div className={`admin-message ${message.includes('Ошибка') ? 'error' : 'success'}`}>
            {message}
          </div>
        )}

        <div className="moderation-stats-compact">
          <div className="stat-item-compact">
            <span className="stat-label">Всего пользователей:</span>
            <span className="stat-value">{users.length}</span>
            </div>
          <div className="stat-item-compact">
            <span className="stat-label">Активных:</span>
            <span className="stat-value active">{users.filter(user => user.is_active).length}</span>
            </div>
          <div className="stat-item-compact">
            <span className="stat-label">Заблокированных:</span>
            <span className="stat-value blocked">{users.filter(user => !user.is_active).length}</span>
            </div>
          <div className="stat-item-compact">
            <span className="stat-label">Всего ботов:</span>
            <span className="stat-value">{users.reduce((sum, user) => sum + (user.agents?.length || 0), 0)}</span>
          </div>
        </div>

      {/* Переключатель архив/актив в шапке убран; теперь внутри каждой группы агентов */}

        {/* Поиск по любому полю */}
        <div className="search-container" style={{ marginTop: 12 }}>
          <input
            type="text"
            placeholder="Поиск по любому полю (email, имя, роль, статус, домен, агент и т.д.)"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="search-input"
          />
          <span className="search-results">
            Найдено: {
              users.filter(user => {
                const q = searchTerm.trim().toLowerCase();
                if (!q) return true;
                const parts = [
                  String(user.id || ''),
                  String(user.email || ''),
                  String(user.name || ''),
                  String(user.role_name || user.user_role || ''),
                  user.is_active ? 'активен' : 'заблокирован',
                  user.created_at ? new Date(user.created_at).toLocaleDateString('ru-RU') : ''
                ];
                if (Array.isArray(user.agents)) {
                  user.agents.forEach(a => {
                    parts.push(
                      String(a.name || ''),
                      String(a.domain || ''),
                      a.is_active ? 'бот активен' : 'бот остановлен',
                      a.blocked_by_admin ? 'бот заблокирован' : '' ,
                      a.created_at ? new Date(a.created_at).toLocaleDateString('ru-RU') : ''
                    );
                  });
                }
                const haystack = parts.join(' ').toLowerCase();
                return haystack.includes(q);
              }).length
            } из {users.length}
          </span>
        </div>
      </div>

      <div className="page-content-scrollable">
        <div className="moderation-table-container">
          <div className="table-wrapper">
            <table className="moderation-table">
              <thead>
              <tr>
                <th>Пользователь</th>
                <th>Email</th>
                <th>Роль</th>
                <th>Статус аккаунта</th>
                <th>Боты</th>
                <th>Дата регистрации</th>
                <th>Действия</th>
              </tr>
            </thead>
            <tbody>
              {users
                .filter(u => !u.is_system_admin && u.email !== 'admin@supchats.com')
                .filter(user => {
                  const q = searchTerm.trim().toLowerCase();
                  if (!q) return true;
                  const parts = [
                    String(user.id || ''),
                    String(user.email || ''),
                    String(user.name || ''),
                    String(user.role_name || user.user_role || ''),
                    user.is_active ? 'активен' : 'заблокирован',
                    user.created_at ? new Date(user.created_at).toLocaleDateString('ru-RU') : ''
                  ];
                  if (Array.isArray(user.agents)) {
                    user.agents.forEach(a => {
                      parts.push(
                        String(a.name || ''),
                        String(a.domain || ''),
                        a.is_active ? 'бот активен' : 'бот остановлен',
                        a.blocked_by_admin ? 'бот заблокирован' : '' ,
                        a.created_at ? new Date(a.created_at).toLocaleDateString('ru-RU') : ''
                      );
                    });
                  }
                  const haystack = parts.join(' ').toLowerCase();
                  return haystack.includes(q);
                })
                .map(user => [
                <tr key={user.id} className={!user.is_active ? 'blocked-user' : ''}>
                  <td>
                    <div className="user-info">
                      <strong>{user.name || 'Не указано'}</strong>
                      <small>ID: {user.id}</small>
                    </div>
                  </td>
                  <td>{user.email}</td>
                  <td>
                    <div className="role-info">
                      <span className="role-badge">
                        {user.role_name || 'Free'}
                      </span>
                      <div className="role-limits">
                        <small>
                          Боты: {user.max_agents === -1 ? '∞' : user.max_agents} | 
                          Сообщения: {user.max_messages_per_month === -1 ? '∞' : user.max_messages_per_month}
                        </small>
                        <small>
                          Использовано: {user.current_messages_count || 0}
                        </small>
                      </div>
                    </div>
                  </td>
                  <td>
                    <span className={`status-badge ${user.is_active ? 'active' : 'blocked'}`}>
                      {user.is_active ? '✅ Активен' : '🚫 Заблокирован'}
                    </span>
                  </td>
                  <td>
                            <button
                      className="expand-user-btn"
                      onClick={() => toggleUserExpansion(user.id)}
                      title={expandedUsers.has(user.id) ? 'Скрыть агентов' : 'Показать агентов'}
                            >
                      {user.agents?.length || 0} агентов {expandedUsers.has(user.id) ? '▼' : '▶'}
                            </button>
                  </td>
                  <td>
                    {user.created_at ? new Date(user.created_at).toLocaleDateString('ru-RU') : 'Не указан'}
                  </td>
                  <td>
                    <div className="action-buttons">
                      <button 
                        className={`toggle-user-btn ${user.is_active ? 'block' : 'unblock'}`}
                        onClick={() => toggleUserStatus(user.id, user.is_active)}
                      >
                        {user.is_active ? '🚫 Заблокировать' : '✅ Разблокировать'}
                      </button>
                      <button 
                        className="change-role-btn"
                        onClick={() => openRoleModal(user)}
                      >
                        🎭 Роль
                      </button>
                      <button 
                        className="reset-limits-btn"
                        onClick={() => resetUserLimits(user.id)}
                        title="Сбросить месячные лимиты сообщений"
                      >
                        🔄 Лимиты
                      </button>
                    </div>
                  </td>
                </tr>,
                expandedUsers.has(user.id) && (
                  <tr key={`expanded-${user.id}`} className="agents-expanded-row">
                    <td colSpan="6" className="agents-container">
                      <div className="agents-expanded">
                        <div className="agents-header-row">
                          <h4>Агенты пользователя {user.email}</h4>
                          {/* Локальный переключатель Активные/Архивные для данного пользователя */}
                          <div className="agents-toggle">
                            {['active','archived'].map(mode => (
                              <button
                                key={mode}
                                onClick={() => setArchivedViewByUser(prev => ({ ...prev, [user.id]: mode }))}
                                className={(archivedViewByUser[user.id] || 'active') === mode ? 'active' : ''}
                                title={mode === 'active' ? 'Показать активных' : 'Показать архивных'}
                              >
                                {mode === 'active' ? 'Активные' : 'Архив'}
                              </button>
                            ))}
                          </div>
                        </div>
                        {user.agents?.length > 0 ? (
                          <div className="agents-grid">
                            {user.agents
                              .filter(agent => (archivedViewByUser[user.id] || 'active') === 'archived' ? agent.archived === true : agent.archived !== true)
                              .map(agent => (
                              <div key={agent.id} className="agent-card">
                                <div className="agent-header">
                                  <span className="agent-name">
                                    <TruncatedText text={agent.name} maxLength={20} />
                                  </span>
                                  <span className={`agent-status ${agent.archived ? 'blocked' : (agent.blocked_by_admin ? 'blocked' : (agent.is_active ? 'active' : 'paused'))}`}>
                                    {agent.archived ? '🗄️ Архив' : (agent.blocked_by_admin ? '⛔ Заблокирован админом' : (agent.is_active ? '✅ Активен' : '⏸️ Остановлен'))}
                                  </span>
                                </div>
                                <div className="agent-details">
                                  <p><strong>Домен:</strong> {agent.domain || 'Не указан'}</p>
                                  <p><strong>Создан:</strong> {agent.created_at ? new Date(agent.created_at).toLocaleDateString('ru-RU') : 'Не указан'}</p>
                                </div>
                                <div className="agent-actions">
                                  {!agent.archived && (
                                    <>
                                      <button
                                        className={`toggle-agent-btn ${agent.blocked_by_admin ? 'unblock' : 'block'}`}
                                        onClick={() => toggleAgentStatus(agent.id, agent.blocked_by_admin)}
                                        title={agent.blocked_by_admin ? 'Разблокировать агента' : 'Заблокировать агента'}
                                      >
                                        {agent.blocked_by_admin ? '✅ Разблокировать' : '🚫 Заблокировать'}
                                      </button>
                                      <button
                                        className={`toggle-agent-status-btn ${agent.is_active ? 'pause' : 'play'}`}
                                        onClick={() => toggleAgentActivation(agent.id, agent.is_active)}
                                        title={agent.is_active ? 'Остановить агента' : 'Запустить агента'}
                                      >
                                        {agent.is_active ? '⏸️ Остановить' : '▶️ Запустить'}
                                      </button>
                                    </>
                                  )}
                                  {agent.archived && (
                                    <button
                                      className="restore-agent-btn"
                                      onClick={() => restoreAgent(agent.id)}
                                      title="Восстановить агента"
                                    >
                                      ♻️ Восстановить
                                    </button>
                                  )}
                                </div>
                              </div>
                            ))}
                          </div>
                        ) : (
                          <p className="no-agents">У пользователя нет агентов</p>
                        )}
                    </div>
                  </td>
                </tr>
                )
              ])}
            </tbody>
          </table>
          </div>
        </div>
      </div>

      {/* Модальное окно для изменения роли */}
      {showRoleModal && selectedUser && (
        <div className="modal-overlay" onClick={() => setShowRoleModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Изменить роль пользователя</h3>
              <button 
                className="modal-close"
                onClick={() => setShowRoleModal(false)}
              >
                ×
              </button>
            </div>
            <div className="modal-body">
              <div className="user-info-modal">
                <p><strong>Пользователь:</strong> {selectedUser.email}</p>
                <p><strong>Текущая роль:</strong> {selectedUser.role_name || 'Free'}</p>
              </div>
              
              <div className="roles-list">
                <h4>Выберите новую роль:</h4>
                {Object.entries(availableRoles).map(([roleKey, roleData]) => (
                  <div 
                    key={roleKey}
                    className={`role-option ${selectedUser.user_role === roleKey ? 'current' : ''}`}
                  >
                    <div className="role-header">
                      <h5>{roleData.name}</h5>
                      <span className="role-price">${roleData.price}/месяц</span>
                    </div>
                    <div className="role-limits">
                      <p>
                        Боты: {roleData.max_agents === -1 ? 'Безлимит' : roleData.max_agents} | 
                        Сообщения: {roleData.max_messages_per_month === -1 ? 'Безлимит' : roleData.max_messages_per_month}
                      </p>
                    </div>
                    {selectedUser.user_role !== roleKey && (
                      <button 
                        className="select-role-btn"
                        onClick={() => updateUserRole(roleKey)}
                      >
                        Выбрать
                      </button>
                    )}
                    {selectedUser.user_role === roleKey && (
                      <span className="current-role-label">Текущая роль</span>
                    )}
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Контейнер для всплывающих уведомлений */}
      <div className="notification-container">
        {notifications.map((notification) => (
          <div key={notification.id} className={`notification ${notification.type}`}>
            <div className="notification-icon">
              {notification.type === 'success' && '✅'}
              {notification.type === 'error' && '❌'}
              {notification.type === 'info' && 'ℹ️'}
              {notification.type === 'warning' && '⚠️'}
            </div>
            <div className="notification-content">
              <div className="notification-title">{notification.title}</div>
              <div className="notification-message">{notification.message}</div>
            </div>
            <button 
              className="notification-close"
              onClick={() => removeNotification(notification.id)}
            >
              ×
            </button>
          </div>
        ))}
      </div>
    </div>
  );
};

export default ModerationPage;

